import { forwardRef, useState, useRef, useCallback, useMemo, useId, useEffect } from 'react';

import { ComboboxTrigger } from './combobox-trigger';
import { ComboboxContent } from './combobox-content';
import { ComboboxSearch } from './combobox-search';
import { ComboboxVirtualList } from './combobox-virtual-list';
import { ComboboxEmpty } from './combobox-empty';
import { ComboboxLoading } from './combobox-loading';
import { ComboboxMultiValue } from './combobox-multi-value';
import { useComboboxSearch } from './hooks/use-combobox-search';
import { useComboboxKeyboard } from './hooks/use-combobox-keyboard';
import { flattenOptions } from './combobox.utils';
import type { ComboboxProps, ComboboxOption } from './combobox.types';

/**
 * Unified Combobox Component
 *
 * A highly composable, accessible combobox component supporting:
 * - Single and multi-select
 * - Search with debounce and fuzzy matching
 * - Virtual scrolling for large datasets (1000+ items)
 * - Keyboard navigation with type-ahead
 * - Async data loading
 * - Grouped options
 * - Custom rendering
 * - React Hook Form integration
 *
 * @example
 * ```tsx
 * // Single select with search
 * <Combobox
 *   options={countries}
 *   value={selectedCountry}
 *   onChange={setSelectedCountry}
 *   search
 *   placeholder="Select country..."
 * />
 *
 * // Multi-select with virtual scrolling
 * <Combobox
 *   multi
 *   options={largeDataset}
 *   value={selectedItems}
 *   onChange={setSelectedItems}
 *   virtual
 *   search={{ debounce: 300, fuzzyThreshold: 0.7 }}
 * />
 * ```
 */
export const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
  <TValue extends string | number = string, TMulti extends boolean = false>(
    {
      // Value & onChange
      value,
      onChange,
      defaultValue,

      // Options
      options = [],

      // Mode
      multi = false as TMulti,

      // Search
      search = false,

      // Virtual scrolling
      virtual = false,

      // Async
      loading = false,
      onScrollToBottom,

      // Appearance
      size = 'md',
      variant = 'outline',
      placeholder = 'Select option...',
      emptyMessage,
      loadingMessage,

      // Label & Helper Text
      label,
      helperText,

      // State
      disabled = false,
      error,
      clearable = false,

      // Multi-select specific
      maxVisible,
      showCount,
      colorPalette,
      multiConfig, // Extract to prevent DOM warning

      // Custom rendering
      renderOption,
      renderValue,

      // Dropdown
      portalTarget,
      zIndex,
      closeOnSelect = !multi,

      // Callbacks
      onOpen,
      onClose,
      onSearchChange,

      // Accessibility
      id: providedId,
      name,
      required,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,

      // Style
      className,
      ...restProps
    }: ComboboxProps<TValue, TMulti>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // Generate unique IDs for ARIA
    const generatedId = useId();
    const id = providedId || generatedId;
    const triggerId = `${id}-trigger`;
    const contentId = `${id}-content`;
    const searchId = `${id}-search`;

    // Refs
    const triggerRef = useRef<HTMLElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const resetFocusRef = useRef<(() => void) | null>(null);

    // Internal state for uncontrolled mode
    const [internalValue, setInternalValue] = useState<TValue | TValue[] | undefined>(defaultValue);

    // Use controlled value if provided, otherwise use internal state
    const currentValue = (value !== undefined ? value : internalValue) as
      | TValue
      | TValue[]
      | undefined;

    // Open/close state
    const [isOpen, setIsOpen] = useState(false);

    // Resolve multi-select config (multiConfig provides defaults for individual props)
    const resolvedMaxVisible = maxVisible ?? multiConfig?.max;
    const resolvedShowCount = showCount ?? multiConfig?.showCount;
    const resolvedColorPalette = colorPalette ?? multiConfig?.badgeColorScheme;
    const resolvedClearable = clearable || multiConfig?.clearable || false;

    // Search hook
    const { query, setQuery, clearQuery, filteredOptions, isSearchEnabled, isSearching } =
      useComboboxSearch({
        options,
        searchConfig: search,
      });

    // Flatten options for keyboard navigation
    const flatOptions = useMemo(() => flattenOptions(filteredOptions), [filteredOptions]);

    // Open dropdown (currently not used after handleToggle refactor, kept for future API)
    const _handleOpen = useCallback(() => {
      if (disabled) return;

      setIsOpen(true);
      onOpen?.();

      // Focus search input if search is enabled
      setTimeout(() => {
        if (isSearchEnabled && searchRef.current) {
          searchRef.current.focus();
        }
      }, 0);
    }, [disabled, isSearchEnabled, onOpen]);

    // Keyboard navigation hook
    const { focusedIndex, handleKeyDown, resetFocus } = useComboboxKeyboard({
      options: flatOptions,
      isOpen,
      onSelect: handleSelectOption,
      onClose: () => setIsOpen(false), // Simple callback to avoid circular dependency
      loop: true,
      typeAhead: !isSearchEnabled, // Disable type-ahead if search is enabled
    });

    // Store resetFocus in ref to avoid circular dependency in handleClose
    useEffect(() => {
      resetFocusRef.current = resetFocus;
    }, [resetFocus]);

    // Close dropdown
    const handleClose = useCallback(() => {
      setIsOpen(false);
      resetFocusRef.current?.();
      clearQuery();
      onClose?.();

      // Return focus to trigger
      setTimeout(() => {
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
      }, 0);
    }, [clearQuery, onClose]);

    // Toggle dropdown
    const handleToggle = useCallback(() => {
      setIsOpen((prev) => {
        if (prev) {
          // Closing - call handleClose logic
          resetFocusRef.current?.();
          clearQuery();
          onClose?.();
          setTimeout(() => {
            if (triggerRef.current) {
              triggerRef.current.focus();
            }
          }, 0);
        } else {
          // Opening - call handleOpen logic
          if (disabled) return prev; // Don't toggle if disabled

          onOpen?.();
          setTimeout(() => {
            if (isSearchEnabled && searchRef.current) {
              searchRef.current.focus();
            }
          }, 0);
        }

        return !prev;
      });
    }, [disabled, isSearchEnabled, onOpen, onClose, clearQuery]);

    // Select option
    function handleSelectOption(option: ComboboxOption<TValue>) {
      if (option.disabled) return;

      if (multi) {
        // Multi-select mode
        const currentValues = (currentValue as TValue[]) || [];
        const isSelected = currentValues.includes(option.value);

        const newValues = isSelected
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value];

        if (onChange) {
          (onChange as (value: TValue[]) => void)(newValues);
        } else {
          setInternalValue(newValues as TValue | TValue[]);
        }

        // Close dropdown if closeOnSelect is true
        if (closeOnSelect) {
          handleClose();
        }
      } else {
        // Single-select mode
        if (onChange) {
          (onChange as (value: TValue) => void)(option.value);
        } else {
          setInternalValue(option.value as TValue | TValue[]);
        }

        // Close dropdown
        if (closeOnSelect) {
          handleClose();
        }
      }
    }

    // Clear selection
    const handleClear = useCallback(() => {
      if (multi) {
        if (onChange) {
          (onChange as (value: TValue[]) => void)([]);
        } else {
          setInternalValue([] as TValue[]);
        }
      } else {
        if (onChange) {
          (onChange as (value: TValue | undefined) => void)(undefined);
        } else {
          setInternalValue(undefined);
        }
      }
    }, [multi, onChange]);

    // Remove item from multi-select
    const handleRemoveMultiValue = useCallback(
      (valueToRemove: TValue) => {
        const currentValues = (currentValue as TValue[]) || [];
        const newValues = currentValues.filter((v) => v !== valueToRemove);

        if (onChange) {
          (onChange as (value: TValue[]) => void)(newValues);
        } else {
          setInternalValue(newValues as TValue | TValue[]);
        }
      },
      [currentValue, onChange]
    );

    // Get selected options
    const selectedOptions = useMemo(() => {
      if (!currentValue) return [];

      const flatAllOptions = flattenOptions(options);

      if (multi) {
        const values = currentValue as TValue[];
        return flatAllOptions.filter((opt) => values.includes(opt.value));
      } else {
        const singleValue = currentValue as TValue;
        const option = flatAllOptions.find((opt) => opt.value === singleValue);
        return option ? [option] : [];
      }
    }, [currentValue, options, multi]);

    // Check if has value
    const hasValue = multi ? selectedOptions.length > 0 : selectedOptions.length === 1;

    // Render trigger value
    const triggerValue = useMemo(() => {
      if (!hasValue) return null;

      if (renderValue) {
        // Type narrowing based on multi flag to satisfy TMulti conditional type
        if (multi) {
          // Multi-select mode: renderValue expects TValue[]
          return (
            renderValue as (
              value: TValue[],
              selectedOptions: ComboboxOption<TValue>[]
            ) => React.ReactNode
          )(currentValue as TValue[], selectedOptions);
        } else {
          // Single-select mode: renderValue expects TValue
          return (
            renderValue as (
              value: TValue,
              selectedOptions: ComboboxOption<TValue>[]
            ) => React.ReactNode
          )(currentValue as TValue, selectedOptions);
        }
      }

      if (multi) {
        return (
          <ComboboxMultiValue<TValue>
            options={selectedOptions}
            onRemove={handleRemoveMultiValue}
            size={size}
            colorPalette={
              resolvedColorPalette as 'gray' | 'blue' | 'green' | 'purple' | 'red' | 'yellow'
            }
            maxVisible={resolvedMaxVisible}
            showCount={resolvedShowCount}
            disabled={disabled}
          />
        );
      } else {
        return <span className="truncate">{selectedOptions[0]?.label}</span>;
      }
    }, [
      hasValue,
      selectedOptions,
      renderValue,
      multi,
      handleRemoveMultiValue,
      size,
      resolvedColorPalette,
      resolvedMaxVisible,
      resolvedShowCount,
      disabled,
      currentValue,
    ]);

    // Selected values for virtual list
    const selectedValues = useMemo(() => {
      if (multi) {
        return (currentValue as TValue[]) || [];
      } else {
        return currentValue ? [currentValue as TValue] : [];
      }
    }, [currentValue, multi]);

    // Virtual scrolling config
    const virtualConfig = useMemo(() => {
      if (virtual === false) return null;
      if (virtual === true) return { enabled: true };
      return virtual;
    }, [virtual]);

    const itemHeight = virtualConfig?.itemHeight ?? 40;
    const containerHeight = virtualConfig?.containerHeight ?? 300;
    const overscan = virtualConfig?.overscan ?? 5;

    // Render list content
    const renderListContent = () => {
      // Loading state
      if (loading) {
        return <ComboboxLoading message={loadingMessage} size={size} />;
      }

      // Empty state
      if (flatOptions.length === 0) {
        return <ComboboxEmpty query={query} message={emptyMessage} size={size} />;
      }

      // Use virtual list for large datasets or when explicitly enabled
      if (virtual || flatOptions.length > 50) {
        return (
          <ComboboxVirtualList<TValue>
            options={flatOptions}
            selectedValues={selectedValues}
            focusedIndex={focusedIndex}
            onSelect={handleSelectOption}
            itemHeight={itemHeight}
            containerHeight={containerHeight}
            overscan={overscan}
            size={size}
            renderOption={renderOption}
            onScrollToBottom={onScrollToBottom}
          />
        );
      }

      // Regular list (no virtualization)
      return (
        <div style={{ maxHeight: '300px', overflowY: 'auto' }} role="listbox">
          {flatOptions.map((option, index) => {
            const isSelected = selectedValues.includes(option.value);
            const isFocused = focusedIndex === index;

            return (
              <div key={String(option.value)}>
                {renderOption ? (
                  renderOption(option, {
                    isSelected,
                    isFocused,
                    isDisabled: option.disabled || false,
                    isHighlighted: false,
                  })
                ) : (
                  <div
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelectOption(option)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? '#eff6ff'
                        : isFocused
                          ? '#f3f4f6'
                          : 'transparent',
                    }}
                  >
                    {option.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    // Generate IDs for accessibility
    const labelId = label ? `${id}-label` : undefined;
    const helperTextId = helperText ? `${id}-helper` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    // Build aria-describedby from helper text and error
    const computedAriaDescribedBy =
      ariaDescribedBy || [helperTextId, errorId].filter(Boolean).join(' ') || undefined;

    // Size-based label classes
    const labelSizeClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    return (
      <div
        ref={ref}
        className={`combobox-root ${className || ''}`}
        style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}
        {...restProps}
      >
        {/* Label */}
        {label && (
          <label
            id={labelId}
            htmlFor={triggerId}
            className={`combobox-label ${labelSizeClasses[size]}`}
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '2px',
            }}
          >
            {label}
            {required && (
              <span
                className="combobox-required"
                style={{ color: '#ef4444', marginLeft: '4px' }}
                aria-hidden="true"
              >
                *
              </span>
            )}
          </label>
        )}

        {/* Hidden input for form submission */}
        {name && (
          <input
            type="hidden"
            name={name}
            value={multi ? JSON.stringify(currentValue || []) : String(currentValue || '')}
            required={required}
          />
        )}

        {/* Trigger */}
        <ComboboxTrigger
          ref={triggerRef as React.Ref<HTMLButtonElement>}
          id={triggerId}
          isOpen={isOpen}
          disabled={disabled}
          loading={loading || isSearching}
          error={!!error}
          hasValue={hasValue}
          size={size}
          variant={variant}
          placeholder={placeholder}
          onClick={handleToggle}
          onClear={handleClear}
          clearable={resolvedClearable}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={contentId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-labelledby={labelId || ariaLabelledBy}
          aria-describedby={computedAriaDescribedBy}
          aria-invalid={ariaInvalid ?? !!error}
          aria-required={required}
          onKeyDown={handleKeyDown}
        >
          {triggerValue}
        </ComboboxTrigger>

        {/* Helper Text */}
        {helperText && !error && (
          <p
            id={helperTextId}
            className={`combobox-helper ${labelSizeClasses[size]}`}
            style={{ color: '#6b7280', marginTop: '2px' }}
          >
            {helperText}
          </p>
        )}

        {/* Error Message */}
        {error && (
          <p
            id={errorId}
            className={`combobox-error ${labelSizeClasses[size]}`}
            style={{ color: '#dc2626', marginTop: '2px' }}
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Dropdown Content */}
        <ComboboxContent
          id={contentId}
          isOpen={isOpen}
          triggerRef={triggerRef as React.RefObject<HTMLElement>}
          portalTarget={portalTarget}
          zIndex={zIndex}
          size={size}
          onClose={handleClose}
          role="listbox"
          aria-labelledby={labelId || triggerId}
        >
          {/* Search */}
          {isSearchEnabled && (
            <ComboboxSearch
              ref={searchRef}
              id={searchId}
              value={query}
              onChange={(q: string) => {
                setQuery(q);
                onSearchChange?.(q);
              }}
              size={size}
              placeholder={typeof search === 'object' ? search.placeholder : undefined}
            />
          )}

          {/* Options List */}
          {renderListContent()}
        </ComboboxContent>
      </div>
    );
  }
) as (<TValue extends string | number = string, TMulti extends boolean = false>(
  props: ComboboxProps<TValue, TMulti> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement) & { displayName?: string };

Combobox.displayName = 'Combobox';
